/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { AnimationFile, AnimationVariant, RendererSettings } from "./lottie-animation.types";

export const lottieFileToVariant = (file?: AnimationFile): AnimationVariant | undefined =>
	file?.replace("v03_", "") as AnimationVariant;

export const firstSymbolMap = {
	"6": "FivePlanets",
	"7": "StarClover",
	"8": "SunMoon",
	"9": "MoonStar",
} as const;

export const secondSymbolMap = {
	a: "PurpleBlueGreen",
	b: "PinkPeach",
	c: "BlueGreen",
	d: "OrangeYellow",
} as const;

export type FirstSymbolMap = typeof firstSymbolMap;
export type SecondSymbolMap = typeof secondSymbolMap;

export type AnimationName<T extends AnimationVariant> =
	T extends `${infer N extends keyof FirstSymbolMap}${infer L extends keyof SecondSymbolMap}`
		? `${FirstSymbolMap[N]}_${SecondSymbolMap[L]}_${T}`
		: never;
export type AnimationNames = AnimationName<AnimationVariant>;

export const lottieVariantToName = <T extends AnimationVariant>(item?: T): AnimationName<T> | undefined => {
	const trimmedItem = item && item?.trim();
	if (trimmedItem?.length !== 2) return;
	const [first, second] = trimmedItem.split("");
	return `${firstSymbolMap[first as keyof FirstSymbolMap]}_${secondSymbolMap[second as keyof SecondSymbolMap]}_${trimmedItem}` as AnimationName<T>;
};

export const lottieFileToName = (file?: AnimationFile): AnimationNames | undefined =>
	lottieVariantToName(lottieFileToVariant(file));

export const lottieNameToVariant = <T extends AnimationNames>(item?: T): AnimationVariant | undefined => {
	const trimmedItem = item && item?.trim();
	if (!trimmedItem?.length || trimmedItem?.length < 2) return;
	return trimmedItem.slice(-2) as AnimationVariant;
};

export const lottieNameToFile = (item?: AnimationNames): string => `v03_${lottieNameToVariant(item)}`;

export const stretchLottieSetting: RendererSettings = { preserveAspectRatio: "none" };
